<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <v-tabs v-model="tab" background-color="transparent" color="primary">
          <v-tab>PO CRM</v-tab>
          <v-tab>Job</v-tab>
          <v-spacer></v-spacer>
          <div class="caption grey--text text--darken-1 align-self-center mr-4">
            <span v-if="lastRun">
              Perhitungan terakhir: {{ formatDateTime(lastRun.finishedAt || lastRun.startedAt) }} ·
              <v-chip x-small :color="runColor(lastRun.status)" dark>{{ lastRun.status }}</v-chip>
              <span
                v-if="lastRun.errorSummary"
                class="error--text ml-1"
                :title="lastRun.errorSummary"
                >ada job/PO dilewati</span
              >
            </span>
            <span v-else>Belum ada perhitungan</span>
          </div>
        </v-tabs>
        <v-divider></v-divider>

        <v-card-title>
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="2">
              <v-autocomplete
                outlined
                flat
                dense
                hide-details
                :items="dropdownYear"
                item-text="name"
                item-value="year"
                label="Tahun PO"
                background-color="white"
                v-model="filters.year"
                @change="reloadFromFirstPage"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filters.customer"
                label="Customer"
                outlined
                dense
                hide-details
                clearable
                @change="reloadFromFirstPage"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filters.sales"
                label="Sales"
                outlined
                dense
                hide-details
                clearable
                @change="reloadFromFirstPage"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filters.band"
                :items="bandItems"
                label="Band"
                outlined
                dense
                hide-details
                @change="reloadFromFirstPage"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.q"
                append-icon="mdi-magnify"
                label="Cari nomor PO"
                single-line
                hide-details
                outlined
                dense
                clearable
                @keyup.enter="reloadFromFirstPage"
                @click:append="reloadFromFirstPage"
                @click:clear="clearSearch"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="1" class="text-right">
              <v-btn icon color="primary" :to="'/setting/budget-monitoring'" title="Setting">
                <v-icon>mdi-cog</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-checkbox
                v-model="filters.includeClosed"
                label="termasuk PO closed / tidak aktif"
                dense
                hide-details
                class="mt-0"
                @change="reloadFromFirstPage"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-checkbox
                v-model="filters.lowConfidence"
                label="hanya low confidence (alokasi estimasi tinggi)"
                dense
                hide-details
                class="mt-0"
                @change="reloadFromFirstPage"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="12" md="4" class="text-right">
              <v-btn outlined color="primary" :loading="recomputing" @click="recomputeAll">
                <v-icon left>mdi-refresh</v-icon>Hitung ulang semua
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-tabs-items v-model="tab">
          <!-- ============================ TAB PO CRM ============================ -->
          <v-tab-item>
            <v-card-text>
              <v-alert
                v-if="noJob.amount > 0"
                type="info"
                dense
                text
                class="mb-3"
                icon="mdi-information-outline"
              >
                Belanja &amp; stok tanpa PO CRM (job tanpa tautan PO):
                <strong>Rp {{ formatIdr(noJob.amount) }}</strong>
                <a class="ml-2" @click="openNoJob">lihat</a>
              </v-alert>

              <v-data-table
                :headers="posHeaders"
                :items="pos"
                :options.sync="options"
                :server-items-length="posTotal"
                :loading="loading"
                :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                :item-class="rowClass"
                item-key="crmPoId"
                show-expand
                disable-sort
                dense
                class="elevation-1"
              >
                <template v-slot:item.poNumber="{ item }">
                  <router-link
                    :to="'/report/budget-monitoring/po/' + item.crmPoId"
                    class="font-weight-bold"
                  >
                    {{ item.poNumber }}
                  </router-link>
                  <div
                    class="caption grey--text text--darken-1 text-truncate"
                    style="max-width: 220px"
                  >
                    {{ item.title }}
                  </div>
                </template>
                <template v-slot:item.sales="{ item }">
                  <span class="caption">{{ item.sales || "—" }}</span>
                </template>
                <template v-slot:item.jobCount="{ item }">
                  {{ item.jobCount }}
                  <v-icon
                    v-if="!item.jobCount"
                    small
                    color="warning"
                    title="PO belum punya job di PPIC"
                    >mdi-alert-outline</v-icon
                  >
                </template>
                <template v-slot:item.budgetIdr="{ item }">
                  <span v-if="item.band !== 'NO_BUDGET'">{{ formatIdr(item.budgetIdr) }}</span>
                  <v-chip
                    v-else
                    outlined
                    color="primary"
                    x-small
                    :to="'/setting/budget-monitoring?po=' + item.crmPoId"
                    >isi margin</v-chip
                  >
                </template>
                <template v-slot:item.spendIdr="{ item }">
                  {{ formatIdr(item.spendIdr) }}
                  <v-chip
                    x-small
                    color="blue lighten-5"
                    class="blue--text text--darken-2 ml-1"
                    @click.stop="openBreakdown(item, 'SPEND')"
                    >{{ item.spendPoCount }} PO</v-chip
                  >
                </template>
                <template v-slot:item.stockIdr="{ item }">
                  {{ formatIdr(item.stockIdr) }}
                  <v-chip
                    x-small
                    color="indigo lighten-5"
                    class="indigo--text ml-1"
                    @click.stop="openBreakdown(item, 'STOCK')"
                    >{{ item.stockMrCount }} MR</v-chip
                  >
                </template>
                <template v-slot:item.usageIdr="{ item }">
                  <strong>{{ formatIdr(item.usageIdr) }}</strong>
                  <v-icon
                    v-if="item.fxMissing"
                    small
                    color="error"
                    class="ml-1"
                    title="kurs tidak ditemukan"
                    >mdi-currency-usd-off</v-icon
                  >
                </template>
                <template v-slot:item.estimatedPercent="{ item }">
                  <v-icon v-if="item.lowConfidence" small color="error" title="low confidence"
                    >mdi-approximately-equal</v-icon
                  >
                  {{ formatPct(item.estimatedPercent) }}
                </template>
                <template v-slot:item.ratioPercent="{ item }">
                  <strong>{{ formatPct(item.ratioPercent) }}</strong>
                </template>
                <template v-slot:item.band="{ item }">
                  <v-chip small dark :color="bandColor(item.band)">{{
                    bandLabel(item.band)
                  }}</v-chip>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="grey lighten-5 py-2">
                    <v-row dense>
                      <v-col cols="12" md="8" class="caption">
                        <div>
                          <strong>Subtotal</strong> {{ item.currency }}
                          {{ formatIdr(item.subtotal) }}
                          <span v-if="item.currency !== 'IDR'">
                            = Rp {{ formatIdr(item.subtotalIdr) }}</span
                          >
                          · <strong>Margin</strong> {{ formatPct(item.marginPercent) }} ({{
                            marginSourceLabel(item.marginSource)
                          }}) · <strong>Committed</strong> Rp {{ formatIdr(item.committedIdr) }}
                        </div>
                        <div>
                          <strong>Job</strong> {{ item.jobIds || "—" }} · <strong>Dihitung</strong>
                          {{ formatDateTime(item.computedAt) }} · <strong>Dicek</strong>
                          {{ formatDateTime(item.checkedAt) }}
                          <span v-if="item.lastNotifiedBand">
                            · <strong>Notifikasi terakhir</strong>
                            {{ bandLabel(item.lastNotifiedBand) }}
                            {{ formatDateTime(item.lastNotifiedAt) }}</span
                          >
                        </div>
                        <div v-if="Number(item.unlinkedItemShare) > 0">
                          <v-icon x-small color="warning">mdi-link-off</v-icon>
                          {{ formatPct(item.unlinkedItemShare * 100) }} nilai item PO belum
                          terhubung ke job
                        </div>
                      </v-col>
                      <v-col cols="12" md="4" class="text-right">
                        <v-btn
                          small
                          color="primary"
                          text
                          :to="'/report/budget-monitoring/po/' + item.crmPoId"
                        >
                          Buka detail<v-icon right small>mdi-arrow-right</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>
          </v-tab-item>

          <!-- ============================ TAB JOB ============================ -->
          <v-tab-item>
            <v-card-text>
              <v-alert type="info" dense text class="mb-3">
                Band per job bersifat informasi; email dikirim per PO CRM. Budget job = Σ share
                budget PO yang terhubung (§5.2).
              </v-alert>
              <v-data-table
                :headers="jobHeaders"
                :items="filteredJobs"
                :loading="loadingJobs"
                :items-per-page="25"
                :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
                :item-class="jobRowClass"
                item-key="jobId"
                dense
                class="elevation-1"
              >
                <template v-slot:item.jobName="{ item }">
                  <router-link
                    :to="'/report/budget-monitoring/job/' + item.jobId"
                    class="font-weight-bold"
                  >
                    {{ item.jobName || "Job " + item.jobId }}
                  </router-link>
                </template>
                <template v-slot:item.poShares="{ item }">
                  <v-chip
                    v-for="s in item.poShares || []"
                    :key="s.crmPoId"
                    x-small
                    class="mr-1 mb-1"
                    :color="'blue-grey lighten-5'"
                    :to="'/report/budget-monitoring/po/' + s.crmPoId"
                  >
                    {{ s.poNumber || "#" + s.crmPoId }} · Rp {{ formatIdr(s.budgetIdr) }}
                  </v-chip>
                  <span v-if="!(item.poShares || []).length" class="grey--text">—</span>
                </template>
                <template v-slot:item.budgetIdr="{ item }">{{
                  formatIdr(item.budgetIdr)
                }}</template>
                <template v-slot:item.spendIdr="{ item }">
                  {{ formatIdr(item.spendIdr) }}
                  <v-chip
                    x-small
                    color="blue lighten-5"
                    class="blue--text text--darken-2 ml-1"
                    @click.stop="openJobBreakdown(item, 'SPEND')"
                    >rincian</v-chip
                  >
                </template>
                <template v-slot:item.stockIdr="{ item }">
                  {{ formatIdr(item.stockIdr) }}
                  <v-chip
                    x-small
                    color="indigo lighten-5"
                    class="indigo--text ml-1"
                    @click.stop="openJobBreakdown(item, 'STOCK')"
                    >rincian</v-chip
                  >
                </template>
                <template v-slot:item.usageIdr="{ item }">
                  <strong>{{ formatIdr(item.usageIdr) }}</strong>
                </template>
                <template v-slot:item.ratioPercent="{ item }">{{
                  formatPct(item.ratioPercent)
                }}</template>
                <template v-slot:item.bandInfo="{ item }">
                  <v-chip small outlined :color="bandColor(item.bandInfo)">{{
                    bandLabel(item.bandInfo)
                  }}</v-chip>
                </template>
              </v-data-table>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-col>

    <budget-spend-breakdown v-model="showBreakdown" :context="breakdown"></budget-spend-breakdown>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Tutup</v-btn>
      </template>
    </v-snackbar>
  </v-row>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import BudgetSpendBreakdown from "@/components/budget/BudgetSpendBreakdown.vue";
import {
  BAND_FILTER_ITEMS,
  bandColor,
  bandLabel,
  bandRowClass,
  formatIdr,
  formatPct,
  marginSourceLabel,
  sumAmount,
} from "@/utils/budgetMonitor";

export default {
  name: "budget-monitoring",
  components: { BudgetSpendBreakdown },
  data: () => ({
    tab: 0,
    loading: false,
    loadingJobs: false,
    recomputing: false,
    filters: {
      year: 0,
      customer: "",
      sales: "",
      band: "",
      q: "",
      includeClosed: false,
      lowConfidence: false,
    },
    options: { page: 1, itemsPerPage: 25 },
    bandItems: BAND_FILTER_ITEMS,
    noJob: { amount: 0 },
    showBreakdown: false,
    breakdown: { bucket: "SPEND", expectedAmount: 0 },
    snackbar: { show: false, text: "", color: "primary" },
    posHeaders: [
      { text: "", value: "data-table-expand", sortable: false },
      { text: "PO CRM", value: "poNumber" },
      { text: "Customer", value: "customer" },
      { text: "Sales", value: "sales" },
      { text: "Job", value: "jobCount", align: "end" },
      { text: "Budget (IDR)", value: "budgetIdr", align: "end" },
      { text: "Belanja PO (IDR)", value: "spendIdr", align: "end" },
      { text: "Stok (IDR)", value: "stockIdr", align: "end" },
      { text: "Pemakaian", value: "usageIdr", align: "end" },
      { text: "Est.", value: "estimatedPercent", align: "end" },
      { text: "Rasio", value: "ratioPercent", align: "end" },
      { text: "Band", value: "band" },
    ],
    jobHeaders: [
      { text: "Job", value: "jobName" },
      { text: "Customer", value: "customer" },
      { text: "PO CRM (share budget)", value: "poShares", sortable: false },
      { text: "Budget (Σ share)", value: "budgetIdr", align: "end" },
      { text: "Belanja PO", value: "spendIdr", align: "end" },
      { text: "Stok", value: "stockIdr", align: "end" },
      { text: "Pemakaian", value: "usageIdr", align: "end" },
      { text: "Rasio", value: "ratioPercent", align: "end" },
      { text: "Band (info)", value: "bandInfo" },
    ],
  }),
  computed: {
    ...mapState("budgetMonitor", ["pos", "posTotal", "jobs"]),
    ...mapGetters("budgetMonitor", ["lastRun"]),
    dropdownYear() {
      const year = new Date().getFullYear();
      const result = [{ name: "Semua", year: 0 }];
      for (let i = 0; i <= 3; i++) {
        result.push({ name: "" + (year - i), year: year - i });
      }
      return result;
    },
    filteredJobs() {
      const q = (this.filters.q || "").toLowerCase();
      const band = this.filters.band;
      return (this.jobs || []).filter(j => {
        if (band && j.bandInfo !== band) return false;
        if (!q) return true;
        return (
          (j.jobName || "").toLowerCase().includes(q) ||
          (j.customer || "").toLowerCase().includes(q) ||
          String(j.jobId).includes(q)
        );
      });
    },
  },
  watch: {
    options: {
      handler() {
        this.loadPos();
      },
      deep: true,
    },
    tab(v) {
      if (v === 1 && !this.jobs.length) this.loadJobs();
    },
  },
  created() {
    this.$store.dispatch("budgetMonitor/fetchRuns").catch(() => {});
    this.loadNoJob();
  },
  methods: {
    formatIdr,
    formatPct,
    bandColor,
    bandLabel,
    marginSourceLabel,
    rowClass(item) {
      return bandRowClass(item.band);
    },
    jobRowClass(item) {
      return bandRowClass(item.bandInfo);
    },
    runColor(status) {
      return status === "DONE"
        ? "success"
        : status === "PARTIAL"
        ? "warning"
        : status === "RUNNING"
        ? "primary"
        : "error";
    },
    formatDateTime(d) {
      if (!d) return "—";
      const s = typeof d === "string" ? d : new Date(d).toISOString();
      return s.substring(0, 16).replace("T", " ");
    },
    clearSearch() {
      this.filters.q = "";
      this.reloadFromFirstPage();
    },
    reloadFromFirstPage() {
      if (this.options.page !== 1) {
        this.options.page = 1; // watcher reloads
      } else {
        this.loadPos();
      }
      if (this.tab === 1) this.loadJobs();
    },
    async loadPos() {
      this.loading = true;
      try {
        await this.$store.dispatch("budgetMonitor/fetchPos", {
          year: this.filters.year || undefined,
          band: this.filters.band || undefined,
          customer: this.filters.customer || undefined,
          sales: this.filters.sales || undefined,
          q: this.filters.q || undefined,
          includeClosed: this.filters.includeClosed,
          lowConfidence: this.filters.lowConfidence,
          page: this.options.page - 1,
          size: this.options.itemsPerPage > 0 ? this.options.itemsPerPage : 100,
        });
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal memuat daftar PO" });
      } finally {
        this.loading = false;
      }
    },
    async loadJobs() {
      this.loadingJobs = true;
      try {
        await this.$store.dispatch("budgetMonitor/fetchJobs", {});
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal memuat daftar job" });
      } finally {
        this.loadingJobs = false;
      }
    },
    async loadNoJob() {
      try {
        const groups = await this.$store.dispatch("budgetMonitor/fetchNoJobLines", null);
        const lines = (groups || []).reduce((acc, g) => acc.concat(g.lines || []), []);
        this.noJob.amount = sumAmount(lines.filter(l => l.bucket !== "COMMITTED"));
      } catch (e) {
        this.noJob.amount = 0;
      }
    },
    openBreakdown(row, bucket) {
      this.breakdown = {
        crmPoId: row.crmPoId,
        bucket,
        expectedAmount: bucket === "STOCK" ? row.stockIdr : row.spendIdr,
        poNumber: row.poNumber,
      };
      this.showBreakdown = true;
    },
    openJobBreakdown(row, bucket) {
      this.breakdown = {
        jobId: row.jobId,
        jobName: row.jobName || "Job " + row.jobId,
        bucket,
        expectedAmount: bucket === "STOCK" ? row.stockIdr : row.spendIdr,
      };
      this.showBreakdown = true;
    },
    openNoJob() {
      this.breakdown = {
        jobId: null,
        jobName: "tanpa PO CRM",
        bucket: "SPEND",
        expectedAmount: this.noJob.amount,
      };
      this.showBreakdown = true;
    },
    async recomputeAll() {
      this.recomputing = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/recomputeAll");
        this.notify(
          res && res.started
            ? "Perhitungan dimulai di latar belakang"
            : "Perhitungan sedang berjalan",
          "primary"
        );
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.notify(
          status === 409 ? "Perhitungan sedang berjalan" : "Gagal memulai perhitungan",
          status === 409 ? "warning" : "error"
        );
      } finally {
        this.recomputing = false;
        this.$store.dispatch("budgetMonitor/fetchRuns").catch(() => {});
      }
    },
    notify(text, color) {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style>
.row-green {
  background: #e8f5e9;
}
.row-yellow {
  background: #fff8e1;
}
.row-red {
  background: #ffebee;
}
.row-none {
  background: #eeeeee;
}
</style>
